/**
 * Assembles the document rendered inside the email iframe (#363).
 *
 * The fragment is sanitized (see `email-html-sanitize.ts`) and then wrapped in a
 * complete document carrying our own stylesheet and height reporter. Because
 * sanitizing strips the email's scripts BEFORE this runs, the only script that
 * reaches the frame is the reporter below — the `allow-scripts` sandbox flag
 * grants our trusted code the ability to measure, not the email anything.
 *
 * Design: docs/superpowers/specs/2026-07-27-email-html-rendering-design.md
 */

import { sanitizeEmailHtml } from './email-html-sanitize';

/**
 * Floor for the iframe height, also used when the frame never reports one
 * (script blocked, malformed document). A frame must never collapse to 0px and
 * silently vanish from the reading pane.
 */
export const EMAIL_FRAME_MIN_HEIGHT = 240;

/** Ceiling, so a hostile or runaway document can't post an absurd height. */
export const EMAIL_FRAME_MAX_HEIGHT = 20000;

/** Identifies our height messages, so unrelated postMessage traffic is ignored. */
export const EMAIL_FRAME_MESSAGE = 'workorder:email-frame-height';

/**
 * Email assumes a white page and typically sets only foreground colours, so the
 * surface is pinned light regardless of the app theme — otherwise dark mode
 * renders black text on a black background. Wide Outlook tables and full-size
 * inline images are constrained to the pane width.
 */
const STYLESHEET = `
	html, body {
		margin: 0;
		padding: 0;
		background: #ffffff;
		color: #1a1a1a;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		font-size: 14px;
		line-height: 1.45;
		word-break: break-word;
	}
	body { padding: 4px 2px 8px; }
	img { max-width: 100%; height: auto; }
	table { max-width: 100%; }
	blockquote {
		margin: 8px 0 8px 8px;
		padding-left: 10px;
		border-left: 2px solid #dcdcdc;
		color: #555;
	}
	a { color: #1a5fb4; }
`;

/**
 * Reports content height to the parent on load, whenever an image finishes
 * arriving (the first measurement is short otherwise), and on any later reflow.
 */
const HEIGHT_REPORTER = `
	(function () {
		var last = 0;
		function report() {
			var h = document.documentElement.scrollHeight;
			if (h === last) return;
			last = h;
			parent.postMessage({ type: '${EMAIL_FRAME_MESSAGE}', height: h }, '*');
		}
		window.addEventListener('load', report);
		document.addEventListener('DOMContentLoaded', report);
		if (window.ResizeObserver) new ResizeObserver(report).observe(document.documentElement);
		Array.prototype.forEach.call(document.images, function (img) {
			img.addEventListener('load', report);
			img.addEventListener('error', report);
		});
		report();
	})();
`;

/**
 * Make bare URLs in the HTML body clickable.
 *
 * Webflow's notification emails print the homeowner's upload links as TEXT, not anchors —
 * so the one thing staff most want to click in an improvement request was the one thing
 * that did nothing. Real inbound mail is full of the same.
 *
 * Runs in the frame as a DOM pass rather than a regex over the HTML string, which is the
 * whole point: a string pass would happily rewrite the inside of an `href` or a `style`
 * attribute. A TreeWalker sees text nodes only, and anchors are skipped so an already
 * linked URL is not nested inside itself. `<base target="_blank">` handles the target.
 *
 * The pattern is a deliberately simplified twin of `utils/linkify.ts` — this script is
 * serialized into the document and cannot import. Only http/https/mailto are ever
 * produced, so no `javascript:` href can be synthesized here.
 */
const LINKIFY = `
	(function () {
		var RE = /(https?:\\/\\/[^\\s<>"']+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,})/gi;
		var SKIP = { A: 1, SCRIPT: 1, STYLE: 1, TEXTAREA: 1 };
		var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
		var nodes = [], node;
		while ((node = walker.nextNode())) {
			if (node.parentElement && SKIP[node.parentElement.tagName]) continue;
			if (RE.test(node.nodeValue || '')) nodes.push(node);
			RE.lastIndex = 0;
		}
		nodes.forEach(function (text) {
			var frag = document.createDocumentFragment();
			var value = text.nodeValue, cursor = 0, m;
			RE.lastIndex = 0;
			while ((m = RE.exec(value))) {
				var raw = m[0];
				// Trailing sentence punctuation is not part of the link.
				var trimmed = raw.replace(/[.,;:!?)\\]}'"]+$/, '');
				if (m.index > cursor) frag.appendChild(document.createTextNode(value.slice(cursor, m.index)));
				var a = document.createElement('a');
				a.href = trimmed.indexOf('@') > -1 && trimmed.indexOf('http') !== 0 ? 'mailto:' + trimmed : trimmed;
				a.textContent = trimmed;
				frag.appendChild(a);
				if (raw.length > trimmed.length) frag.appendChild(document.createTextNode(raw.slice(trimmed.length)));
				cursor = m.index + raw.length;
			}
			if (cursor < value.length) frag.appendChild(document.createTextNode(value.slice(cursor)));
			text.parentNode.replaceChild(frag, text);
		});
	})();
`;

/**
 * Sanitize `html` and wrap it into the full document for the frame.
 * Returns '' when nothing renderable survives, which callers treat as
 * "fall back to the plain-text body".
 */
export function buildEmailDocument(html: string | null | undefined): string {
	const safe = sanitizeEmailHtml(html);
	if (!safe.trim()) return '';

	return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>${STYLESHEET}</style>
</head>
<body>${safe}<script>${LINKIFY}${HEIGHT_REPORTER}</script></body>
</html>`;
}

/**
 * Constrain a height reported by the frame to something sane.
 *
 * Anything that isn't a finite positive number — a hostile document posting
 * `Infinity`, a string, or nothing at all — resolves to the floor rather than
 * being trusted, so the frame stays visible and bounded either way.
 */
export function clampFrameHeight(height: number): number {
	if (typeof height !== 'number' || !Number.isFinite(height)) return EMAIL_FRAME_MIN_HEIGHT;
	return Math.min(Math.max(Math.round(height), EMAIL_FRAME_MIN_HEIGHT), EMAIL_FRAME_MAX_HEIGHT);
}

/** Whether this HTML is worth rendering in a frame at all. */
export function isRenderableEmailHtml(html: string | null | undefined): boolean {
	return sanitizeEmailHtml(html).trim().length > 0;
}
